import React from "react";
import { Link } from "react-router-dom";
import {
  BellRing,
  BriefcaseBusiness,
  CalendarDays,
  ChevronRight,
  Landmark,
  ShieldCheck,
  TrainFront,
} from "lucide-react";

const exams = [
  {
    title: "SSC MTS, CHSL, CGL",
    status: "Coming Soon",
    tag: "Central Govt",
    icon: BriefcaseBusiness,
    theme: "purple",
    cta: "Explore",
  },
  {
    title: "Banking PO & Clerk",
    status: "New Batch",
    tag: "Banking",
    icon: Landmark,
    theme: "amber",
    cta: "Apply Now",
  },
  {
    title: "Railway Group D",
    status: "Practice Live",
    tag: "Railway",
    icon: TrainFront,
    theme: "featured",
    cta: "Apply Now",
    featured: true,
  },
  {
    title: "State PSC",
    status: "Apply Now",
    tag: "PSC",
    icon: Landmark,
    theme: "amber-strong",
    cta: "Apply Now",
  },
  {
    title: "Defence Exams",
    status: "Trending",
    tag: "Defence",
    icon: ShieldCheck,
    theme: "purple-dark",
    cta: "Explore",
  },
];

const cardThemes = {
  purple: {
    shell: "border-bgBlue bg-white",
    top: "bg-gradient-to-br from-[#5d35d6] to-[#7a5af8] text-white",
    badge: "bg-[#6f46eb] text-white",
    button: "border border-[#8d70f6] text-[#6a41e6] hover:bg-[#f5f1ff]",
    iconWrap: "bg-white text-[#6a41e6] shadow-[0_14px_30px_rgba(106,65,230,0.18)]",
  },
  amber: {
    shell: "border-[#ffd7a0] bg-white",
    top: "bg-gradient-to-br from-[#fff8ed] to-[#fff2db] text-[#ff8e1c]",
    badge: "bg-[#ff9b2f] text-white",
    button: "border border-[#ffaf58] text-[#ff8b16] hover:bg-[#fff6ea]",
    iconWrap: "bg-white text-[#ff971f] shadow-[0_14px_30px_rgba(255,151,31,0.20)]",
  },
  featured: {
    shell: "border-[#8d70f6] bg-white shadow-[0_20px_60px_rgba(106,65,230,0.14)]",
    top: "bg-gradient-to-br from-[#f4efff] to-[#ffffff] text-[#6a41e6]",
    badge: "bg-[#6f46eb] text-white",
    button: "bg-gradient-to-r from-[#6a41e6] to-[#7b50f1] text-white hover:opacity-95",
    iconWrap: "bg-white text-[#6a41e6] shadow-[0_18px_34px_rgba(106,65,230,0.22)]",
  },
  "amber-strong": {
    shell: "border-[#ffd29a] bg-white",
    top: "bg-gradient-to-br from-[#ffb22e] to-[#ffc65f] text-white",
    badge: "bg-[#ff9b2f] text-white",
    button: "border border-[#ffaf58] text-[#ff8b16] hover:bg-[#fff6ea]",
    iconWrap: "bg-white text-[#f78a13] shadow-[0_14px_30px_rgba(247,138,19,0.20)]",
  },
  "purple-dark": {
    shell: "border-[#d8cdfa] bg-white",
    top: "bg-gradient-to-br from-[#4a238f] to-[#6d47d7] text-white",
    badge: "bg-[#6f46eb] text-white",
    button: "border border-[#8d70f6] text-[#6a41e6] hover:bg-[#f5f1ff]",
    iconWrap: "bg-white text-[#5b35cf] shadow-[0_14px_30px_rgba(91,53,207,0.20)]",
  },
};

const UpcomingExam = () => {
  return (
    <section
      id="upcoming"
      className="relative w-full overflow-hidden bg-[radial-gradient(circle_at_top,_#f7f3ff_0%,_#ffffff_52%,_#fffaf2_100%)] py-12 font-poppins"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#ffc778] bg-white/90 px-5 py-2 text-sm font-semibold text-[#ff8615] shadow-[0_10px_30px_rgba(255,166,0,0.12)]">
            <BellRing size={16} />
            Trending Now
          </div>

          <h2 className="mt-5 text-3xl font-bold tracking-tight text-[#151b53] sm:text-4xl lg:text-5xl">
            Upcoming <span className="text-[#f7a51b]">Exams</span>
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-[#4c5572] sm:text-base">
            Get ready! Upcoming exams are just around the corner, start your
            preparation today.
          </p>

          <div className="mt-4 flex items-center justify-center gap-2">
            <span className="h-1.5 w-8 rounded-full bg-[#d6d1ea]" />
            <span className="h-1.5 w-10 rounded-full bg-[#6e44ea]" />
            <span className="h-1.5 w-8 rounded-full bg-[#f7a51b]" />
          </div>
        </div>

        <div className="mt-8 flex gap-4 overflow-x-auto pb-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden lg:grid lg:grid-cols-5 lg:overflow-visible">
          {exams.map((exam) => {
            const Icon = exam.icon;
            const theme = cardThemes[exam.theme];

            return (
              <article
                key={exam.title}
                className={`relative min-w-[230px] rounded-[2rem] border ${theme.shell} p-0 shadow-[0_18px_45px_rgba(27,39,94,0.08)]`}
              >
                {exam.featured && (
                  <div className="absolute left-1/2 top-0 z-20 -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-gradient-to-r from-[#6a41e6] to-[#7b50f1] px-4 py-1.5 text-xs font-semibold text-white shadow-[0_10px_24px_rgba(106,65,230,0.25)]">
                    Most Popular
                  </div>
                )}

                <div
                  className={`relative rounded-t-[2rem] px-4 pb-4 pt-4 text-center ${theme.top}`}
                >
                  <div className="absolute inset-x-0 bottom-0 h-8 rounded-b-[45%] bg-white/92" />
                  <div
                    className={`relative z-10 mx-auto flex h-16 w-16 items-center justify-center rounded-full ${theme.iconWrap}`}
                  >
                    <Icon size={30} strokeWidth={2.2} />
                  </div>
                </div>

                <div className="px-4 pb-4 pt-2.5 text-center">
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold ${theme.badge}`}
                  >
                    {exam.tag}
                  </span>

                  <h3 className="mt-3 min-h-[44px] text-[1.2rem] font-bold leading-tight text-[#18214f]">
                    {exam.title}
                  </h3>

                  <div className="mx-auto mt-3 h-px w-full bg-[#ece8f7]" />

                  <div className="mt-3 flex items-center justify-center gap-2 text-sm font-medium text-[#4e5878]">
                    <CalendarDays size={15} className="text-[#7b50f1]" />
                    {exam.status}
                  </div>

                  <Link
                    to="/login"
                    className={`mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold transition ${theme.button}`}
                  >
                    {exam.cta}
                    <ChevronRight size={18} />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default UpcomingExam;
